import { FC, useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Center, Text } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";

export interface HorizontalBarDataPoint {
  count: number;
  label: string;
  tooltipLine?: string;
  value: number;
}

interface HorizontalBarChartProps {
  minHeight?: number;
  /** Minimum chart width in px. The chart fills the available width but
      never shrinks below this — it scrolls horizontally instead. */
  minWidth?: number;
  points: HorizontalBarDataPoint[];
}

const TICK_MUTED = { fontSize: 11, fill: "hsl(220,10%,46%)" };
const TICK_DARK = { fontSize: 11, fill: "hsl(222,20%,16%)" };
const GRID_STROKE = "hsl(220,14%,93%)";
const CHAR_WIDTH = 6.6;
const Y_TICK_LINE_HEIGHT = 12;

/* Wrap a category label into at most two lines that each fit `maxChars`,
   ellipsizing the second line if the label still overflows. Recharts renders
   YAxis category ticks as a single SVG line by default, which clips long
   labels — this lets us use the reserved left gutter fully. */
const wrapToLines = (label: string, maxChars: number): string[] => {
  if (label.length <= maxChars) return [label];
  const lines = ["", ""];
  let idx = 0;
  for (const word of label.split(/\s+/)) {
    const candidate = lines[idx] ? `${lines[idx]} ${word}` : word;
    if (candidate.length > maxChars && idx === 0) {
      idx = 1;
      lines[1] = word;
    } else if (idx === 1 && candidate.length > maxChars) {
      lines[1] = `${lines[1]}…`;
      break;
    } else {
      lines[idx] = candidate;
    }
  }
  return lines.filter((line) => line.length > 0);
};

const YAxisTick = ({
  fill,
  maxChars,
  payload,
  x,
  y,
}: {
  fill: string;
  maxChars: number;
  payload: { value: string };
  x: number;
  y: number;
}) => {
  const lines = wrapToLines(String(payload.value), maxChars);
  const startDy = (-(lines.length - 1) * Y_TICK_LINE_HEIGHT) / 2 + 4;
  return (
    <text
      fill={fill}
      fontSize={11}
      fontWeight={500}
      textAnchor="end"
      x={x}
      y={y}
    >
      {lines.map((line, i) => (
        <tspan dy={i === 0 ? startDy : Y_TICK_LINE_HEIGHT} key={i} x={x}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

function HorizontalTooltip({
  active,
  payload,
  formatPercent,
  primary,
}: {
  active?: boolean;
  formatPercent: (v: number) => string;
  payload?: {
    payload: {
      count: number;
      name: string;
      tooltipLine?: string;
      value: number;
    };
  }[];
  primary: string;
}) {
  if (!active || !payload?.length) return null;
  const { count, name, tooltipLine, value } = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        maxWidth: 240,
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {name}
      </p>
      <p style={{ color: primary, fontSize: 12, margin: "2px 0 0" }}>
        {tooltipLine ?? `${formatPercent(value)} · N=${count}`}
      </p>
    </div>
  );
}

const HorizontalBarChart: FC<HorizontalBarChartProps> = ({
  minHeight = 300,
  minWidth = 480,
  points,
}) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const { chartData, chartHeight, leftPadding } = useMemo(() => {
    if (points.length === 0) {
      return { chartData: [], chartHeight: minHeight, leftPadding: 96 };
    }

    const sorted = [...points].sort((a, b) => b.value - a.value);
    const barHeight = sorted.length <= 5 ? 40 : 28;
    const calculatedHeight = Math.max(
      sorted.length * barHeight + 60,
      minHeight
    );
    // Reserve enough left width to show labels in full across up to two
    // wrapped lines (so ~half the chars sit on each line), capped so the bars
    // keep room.
    const maxLabelLength = Math.max(...sorted.map((p) => p.label.length));
    const calculatedLeftPadding = Math.min(
      Math.max(112, Math.ceil(maxLabelLength / 2) * CHAR_WIDTH + 16),
      220
    );

    const data = sorted.map((p) => ({
      count: p.count,
      name: p.label,
      tooltipLine: p.tooltipLine,
      value: p.value,
    }));

    return {
      chartData: data,
      chartHeight: calculatedHeight,
      leftPadding: calculatedLeftPadding,
    };
  }, [minHeight, points]);

  if (chartData.length === 0) {
    return (
      <Center className="h-[450px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const formatPercent = (value: number) =>
    i18n.numberToPercentage(value, { precision: 0 });

  // Chars each wrapped label line can hold within the reserved left gutter.
  const maxCharsPerLine = Math.max(
    6,
    Math.floor((leftPadding - 16) / CHAR_WIDTH)
  );

  const sharedChartProps = {
    data: chartData,
    layout: "vertical" as const,
    margin: { bottom: 20, left: 10, right: 40, top: 10 },
  };

  const sharedChildren = (
    <>
      <CartesianGrid
        horizontal={false}
        stroke={GRID_STROKE}
        strokeDasharray="3 3"
      />
      <XAxis
        axisLine={false}
        tick={TICK_MUTED}
        tickCount={5}
        tickFormatter={(v) => formatPercent(Math.round(v))}
        tickLine={false}
        type="number"
      />
      <YAxis
        dataKey="name"
        tick={(tickProps) => (
          <YAxisTick
            fill={TICK_DARK.fill}
            maxChars={maxCharsPerLine}
            payload={tickProps.payload}
            x={Number(tickProps.x)}
            y={Number(tickProps.y)}
          />
        )}
        tickLine={false}
        type="category"
        width={leftPadding}
      />
      <Tooltip
        content={
          <HorizontalTooltip
            formatPercent={formatPercent}
            primary={themeColors.primary}
          />
        }
        cursor={{ fill: "hsl(220,14%,97%)" }}
      />
      <Bar
        barSize={chartData.length <= 5 ? 26 : 18}
        dataKey="value"
        fill={themeColors.primary}
        radius={[0, 4, 4, 0]}
      />
    </>
  );

  const needsScroll = chartHeight > 450;
  const displayHeight = needsScroll ? 450 : chartHeight;

  /* Plain `<div>`s for the scroll wrappers (not RN `<View>`/`<Box>`): RN Web
     doesn't expose `overflowX`. The outer wrapper scrolls horizontally and
     `minWidth: 0` lets it shrink below the inner content so the surrounding
     card keeps its flex share; the inner wrapper enforces the chart's minimum
     width and scrolls vertically when there are many bars. */
  return (
    <div style={{ minWidth: 0, overflowX: "auto", width: "100%" }}>
      <div
        style={{
          height: displayHeight,
          minWidth,
          overflowY: needsScroll ? "auto" : "visible",
          width: "100%",
        }}
      >
        <ResponsiveContainer height={chartHeight} minWidth={0} width="100%">
          <RechartsBarChart {...sharedChartProps}>
            {sharedChildren}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HorizontalBarChart;
